import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import { useBudget } from '../../context/BudgetContext';
import { setGoals } from '../../context/budgetActions';
import { PROVINCES } from '../../data/provinces';

export default function StepGoals() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const { state, dispatch } = useBudget();
  const { goals } = state;

  const update = (partial) => dispatch(setGoals(partial));

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#1E3A5F' }}>
          {t('budgetPlanner.goals.title', 'Tus metas financieras')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('budgetPlanner.goals.subtitle', 'Configura tus objetivos para obtener recomendaciones personalizadas.')}
        </Typography>

        <Stack spacing={4}>
          {/* Savings target */}
          <Box>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
              {t('budgetPlanner.goals.savingsTarget', 'Meta de ahorro')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('budgetPlanner.goals.savingsDesc', '¿Qué porcentaje de tus ingresos quieres ahorrar?')}: {goals.savingsTargetPct}%
            </Typography>
            <Slider
              value={goals.savingsTargetPct}
              onChange={(_, val) => update({ savingsTargetPct: val })}
              min={0}
              max={50}
              step={5}
              marks={[
                { value: 10, label: '10%' },
                { value: 20, label: '20%' },
                { value: 30, label: '30%' },
                { value: 50, label: '50%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ color: '#1E3A5F' }}
            />
          </Box>

          {/* Emergency fund */}
          <Box>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
              {t('budgetPlanner.goals.emergencyFund', 'Fondo de emergencia')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('budgetPlanner.goals.emergencyDesc', '¿Cuántos meses de gastos quieres tener en reserva?')}: {goals.emergencyFundMonths} {t('budgetPlanner.common.months', 'meses')}
            </Typography>
            <Slider
              value={goals.emergencyFundMonths}
              onChange={(_, val) => update({ emergencyFundMonths: val })}
              min={1}
              max={12}
              step={1}
              marks={[
                { value: 3, label: '3' },
                { value: 6, label: '6' },
                { value: 9, label: '9' },
                { value: 12, label: '12' },
              ]}
              valueLabelDisplay="auto"
              sx={{ color: '#1E3A5F' }}
            />
          </Box>

          {/* Province */}
          <TextField
            label={t('budgetPlanner.goals.province', 'Provincia')}
            select
            value={goals.province}
            onChange={(e) => update({ province: e.target.value })}
            size="small"
            sx={{ maxWidth: 300 }}
          >
            <MenuItem value="">
              {t('budgetPlanner.goals.selectProvince', 'Selecciona tu provincia')}
            </MenuItem>
            {PROVINCES.map((p) => (
              <MenuItem key={p.code} value={p.code}>
                {p.label[lng] || p.label.es}
              </MenuItem>
            ))}
          </TextField>

          {/* Debt toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={goals.hasDebt}
                onChange={(e) => update({ hasDebt: e.target.checked })}
                sx={{ '& .Mui-checked': { color: '#D32F2F' } }}
              />
            }
            label={t('budgetPlanner.goals.hasDebt', '¿Tienes deudas que quieras planificar?')}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
